/**
 * Pagination Molecule
 *
 * Controle de paginação - Mobile first
 */
import React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import Button from '../atoms/Button'

export default function Pagination({
	page,
	total,
	limit,
	onPageChange,
	showInfo = true,
	className = '',
}) {
	const totalPages = Math.ceil(total / limit)
	const start = (page - 1) * limit + 1
	const end = Math.min(page * limit, total)

	const canGoPrev = page > 1
	const canGoNext = page < totalPages

	// Gerar array de páginas visíveis - menos no mobile
	const getVisiblePages = (isMobile = false) => {
		const pages = []
		const maxVisible = isMobile ? 3 : 5

		if (totalPages <= maxVisible) {
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i)
			}
		} else if (isMobile) {
			// Mobile: show current, prev and next only
			if (page === 1) {
				pages.push(1, 2, '...', totalPages)
			} else if (page === totalPages) {
				pages.push(1, '...', totalPages - 1, totalPages)
			} else {
				pages.push(1, '...', page, '...', totalPages)
			}
		} else {
			if (page <= 3) {
				pages.push(1, 2, 3, 4, '...', totalPages)
			} else if (page >= totalPages - 2) {
				pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages)
			} else {
				pages.push(1, '...', page - 1, page, page + 1, '...', totalPages)
			}
		}

		return pages
	}

	if (total === 0) return null

	return (
		<div className={`flex flex-col sm:flex-row items-center justify-between gap-3 ${className}`}>
			{showInfo && (
				<p className="text-xs sm:text-sm text-text-body text-center sm:text-left">
					<span className="font-medium text-text">{start}</span>-
					<span className="font-medium text-text">{end}</span> de{' '}
					<span className="font-medium text-text">{total}</span>
				</p>
			)}

			<div className="flex items-center gap-1">
				<Button
					variant="outline"
					size="sm"
					onClick={() => onPageChange(page - 1)}
					disabled={!canGoPrev}
					className="p-2"
				>
					<ChevronLeft className="w-4 h-4" />
				</Button>

				{/* Mobile pagination */}
				<div className="flex items-center gap-1 sm:hidden">
					{getVisiblePages(true).map((p, idx) => (
						<React.Fragment key={idx}>
							{p === '...' ? (
								<span className="px-1 text-text-muted text-sm">...</span>
							) : (
								<Button
									variant={p === page ? 'primary' : 'ghost'}
									size="sm"
									onClick={() => onPageChange(p)}
									className="min-w-[32px] px-2"
								>
									{p}
								</Button>
							)}
						</React.Fragment>
					))}
				</div>

				{/* Desktop pagination */}
				<div className="hidden sm:flex items-center gap-1">
					{getVisiblePages(false).map((p, idx) => (
						<React.Fragment key={idx}>
							{p === '...' ? (
								<span className="px-2 text-text-muted">...</span>
							) : (
								<Button
									variant={p === page ? 'primary' : 'ghost'}
									size="sm"
									onClick={() => onPageChange(p)}
								>
									{p}
								</Button>
							)}
						</React.Fragment>
					))}
				</div>

				<Button
					variant="outline"
					size="sm"
					onClick={() => onPageChange(page + 1)}
					disabled={!canGoNext}
					className="p-2"
				>
					<ChevronRight className="w-4 h-4" />
				</Button>
			</div>
		</div>
	)
}
